import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a base axios instance for blog operations
const adminBlogApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
});

// Request interceptor to add token
adminBlogApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Always set Content-Type to application/json since we're not sending files
    config.headers['Content-Type'] = 'application/json';
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminBlogApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminBlogService = {
  // ==================== BLOG CRUD OPERATIONS ====================
  
  /**
   * Get all blogs with filters and pagination
   */
  getBlogs: async (params = {}) => {
    console.log('📤 [BlogService] Fetching blogs...');
    try {
      const response = await adminBlogApi.get('/blogs', { params });
      console.log('📥 [BlogService] Blogs fetched:', response.data);
      return response.data;
    } catch (error) {
      console.log('💥 [BlogService] Error fetching blogs:', error);
      throw error;
    }
  },

  /**
   * Get blog by ID
   */
  getBlogById: async (blogId) => {
    console.log('📤 [BlogService] Fetching blog:', blogId);
    try {
      const response = await adminBlogApi.get(`/blogs/${blogId}`);
      console.log('📥 [BlogService] Blog fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error fetching blog:', error);
      throw error;
    }
  },

  /**
   * Create new blog
   * Note: featuredImage should be the Cloudinary URL string, not a File object
   */
  createBlog: async (blogData) => {
    console.log('📤 [BlogService] Creating blog...');
    try {
      // blogData should already contain the featuredImage URL from Cloudinary
      const response = await adminBlogApi.post('/blogs', blogData);
      console.log('📥 [BlogService] Blog created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error creating blog:', error);
      throw error;
    }
  },

  /**
   * Update blog
   * Note: featuredImage should be the Cloudinary URL string, not a File object
   */
  updateBlog: async (blogId, blogData) => {
    console.log('📤 [BlogService] Updating blog:', blogId);
    try {
      const response = await adminBlogApi.put(`/blogs/${blogId}`, blogData);
      console.log('📥 [BlogService] Blog updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error updating blog:', error);
      throw error;
    }
  },

  /**
   * Update blog status
   */
  updateBlogStatus: async (blogId, status) => {
    console.log('📤 [BlogService] Updating blog status:', blogId, status);
    try {
      const response = await adminBlogApi.patch(`/blogs/${blogId}/status`, { status });
      console.log('📥 [BlogService] Blog status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error updating blog status:', error);
      throw error;
    }
  },

  /**
   * Delete blog (soft delete - archive)
   */
  deleteBlog: async (blogId) => {
    console.log('📤 [BlogService] Deleting blog:', blogId);
    try {
      const response = await adminBlogApi.delete(`/blogs/${blogId}`);
      console.log('📥 [BlogService] Blog deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error deleting blog:', error);
      throw error;
    }
  },

  /**
   * Restore archived blog
   */
  restoreBlog: async (blogId) => {
    console.log('📤 [BlogService] Restoring blog:', blogId);
    try {
      const response = await adminBlogApi.patch(`/blogs/${blogId}/restore`);
      console.log('📥 [BlogService] Blog restored:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error restoring blog:', error);
      throw error;
    }
  },

  /**
   * Permanently delete blog (Super Admin only)
   */
  permanentlyDeleteBlog: async (blogId) => {
    console.log('📤 [BlogService] Permanently deleting blog:', blogId);
    try {
      const response = await adminBlogApi.delete(`/blogs/${blogId}/permanent`);
      console.log('📥 [BlogService] Blog permanently deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error permanently deleting blog:', error);
      throw error;
    }
  },

  /**
   * Bulk update blogs
   */
  bulkUpdateBlogs: async (blogIds, data) => {
    console.log('📤 [BlogService] Bulk updating blogs:', blogIds);
    try {
      const response = await adminBlogApi.post('/blogs/bulk-update', {
        blogIds,
        data
      });
      console.log('📥 [BlogService] Blogs bulk updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error bulk updating blogs:', error);
      throw error;
    }
  },

  // ==================== STATISTICS ====================

  /**
   * Get blog statistics
   */
  getBlogStats: async () => {
    console.log('📤 [BlogService] Fetching blog statistics...');
    try {
      const response = await adminBlogApi.get('/blogs/stats');
      console.log('📥 [BlogService] Blog statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error fetching blog statistics:', error);
      throw error;
    }
  },

  // ==================== COMMENTS MANAGEMENT ====================

  /**
   * Get all comments for a blog
   */
  getBlogComments: async (blogId) => {
    console.log('📤 [BlogService] Fetching comments for blog:', blogId);
    try {
      const response = await adminBlogApi.get(`/blogs/${blogId}/comments`);
      console.log('📥 [BlogService] Comments fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error fetching comments:', error);
      throw error;
    }
  },

  /**
   * Approve comment
   */
  approveComment: async (blogId, commentId) => {
    console.log('📤 [BlogService] Approving comment:', commentId);
    try {
      const response = await adminBlogApi.patch(`/blogs/${blogId}/comments/${commentId}/approve`);
      console.log('📥 [BlogService] Comment approved:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error approving comment:', error);
      throw error;
    }
  },

  /**
   * Delete comment
   */
  deleteComment: async (blogId, commentId) => {
    console.log('📤 [BlogService] Deleting comment:', commentId);
    try {
      const response = await adminBlogApi.delete(`/blogs/${blogId}/comments/${commentId}`);
      console.log('📥 [BlogService] Comment deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error deleting comment:', error);
      throw error;
    }
  },

  // ==================== SEARCH & FILTERS ====================

  /**
   * Search blogs
   */
  searchBlogs: async (query, params = {}) => {
    console.log('📤 [BlogService] Searching blogs:', query);
    try {
      const response = await adminBlogApi.get('/blogs/search', {
        params: { ...params, query }
      });
      console.log('📥 [BlogService] Blog search results:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [BlogService] Error searching blogs:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================

  /**
   * Upload image to Cloudinary
   * This remains separate as it's a direct upload to Cloudinary, not through our backend
   */
  uploadImageToCloudinary: async (file, uploadPreset = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset || process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      return {
        url: response.data.secure_url,
        publicId: response.data.public_id
      };
    } catch (error) {
      console.error('💥 [BlogService] Error uploading to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Format date with time
   */
  formatDateTime: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Get status color for badges
   */
  getStatusColor: (status) => {
    const colors = {
      'Published': 'green',
      'Draft': 'yellow',
      'Scheduled': 'purple',
      'Archived': 'gray'
    };
    return colors[status] || 'gray';
  },

  /**
   * Get status badge configuration
   */
  getStatusBadgeConfig: (status) => {
    const config = {
      Published: {
        textColor: 'text-[#2F8F5B]',
        bgColor: 'bg-[#E7F4ED]',
        dotColor: 'bg-[#2F8F5B]'
      },
      Draft: {
        textColor: 'text-[#B8860B]',
        bgColor: 'bg-[#FFF9E6]',
        dotColor: 'bg-[#B8860B]'
      },
      Scheduled: {
        textColor: 'text-[#412F8F]',
        bgColor: 'bg-[#E8E7F4]',
        dotColor: 'bg-[#412F8F]'
      },
      Archived: {
        textColor: 'text-[#6B635D]',
        bgColor: 'bg-[#F0F0F0]',
        dotColor: 'bg-[#6B635D]'
      }
    };
    return config[status] || config.Draft;
  },

  /**
   * Format views count
   */
  formatViews: (views) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views?.toString() || '0';
  },

  /**
   * Calculate read time from content
   */
  calculateReadTime: (content) => {
    if (!content) return '1min read';
    
    const textLength = content.intro?.length || 0;
    const sectionsLength = content.sections?.reduce((acc, section) => {
      return acc + (section.title?.length || 0) + (section.text?.length || 0);
    }, 0) || 0;
    
    const totalLength = textLength + sectionsLength;
    const wordsPerMinute = 200;
    const wordCount = totalLength / 5; // Approximate word count
    const readMinutes = Math.max(1, Math.ceil(wordCount / wordsPerMinute));
    
    return `${readMinutes}min read`;
  },

  /**
   * Create slug from title
   */
  createSlug: (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  },

  /**
   * Validate if ID is valid MongoDB ObjectId
   */
  isValidObjectId: (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  },

  /**
   * Export blogs
   */
  exportBlogs: async (format = 'csv', filters = {}) => {
    console.log('📤 [BlogService] Exporting blogs...');
    try {
      const response = await adminBlogApi.get('/blogs/export', {
        params: { format, ...filters },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `blogs_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      console.log('📥 [BlogService] Blogs exported');
      return true;
    } catch (error) {
      console.error('💥 [BlogService] Error exporting blogs:', error);
      throw error;
    }
  }
};

export default adminBlogService;